/**
 * "Player iframe events" — the postMessage protocol between the hosted player
 * experience and the page that embeds it: envelope, every outgoing event with
 * the moment it fires, and the messages the page can send back.
 *
 * Keep this table in step with `apps/iframe/src/modules/postMessage/events.ts`
 * — it is the only place partners learn these names. Partner-facing markdown.
 */
export const iframeEvents: string[] = [
  '## Player iframe events',
  '',
  'The hosted player experience tells the page that embeds it what the player is doing, with',
  '`window.postMessage`. Nothing here is required — the money protocol is the wallet callbacks — but it',
  'lets your page react: open your cashier, refresh a balance widget, track conversions, re-launch after',
  'an expiry.',
  '',
  'Every message has the same envelope and is posted to `window.parent` with target origin `*`:',
  '',
  '```json',
  '{ "source": "repackz", "type": "purchase_completed", "payload": { "order_id": "…", "items": [] } }',
  '```',
  '',
  'Always check `source === "repackz"` before reading `type`; if you want to be strict, also compare',
  '`event.origin` with the iframe host you embed.',
  '',
  '| Event | When it fires | `payload` |',
  '| --- | --- | --- |',
  '| `session_ready` | The session is authenticated and the first balance is loaded — safe to show the iframe. | `{ balance_cents }` |',
  '| `session_expired` | The token was missing or rejected at load, a request got `401` mid-session, or the player pressed **Return** on the expired screen. | — |',
  '| `balance_updated` | Every time the balance is (re)read: at load, after a purchase or a sell, after `balance_changed`, when the tab regains focus. After a sell or a canceled delivery the read can reach your `get_balance` before our `/credit` does; when the balance comes back unchanged the game reads it again a few seconds later, so a second `balance_updated` may follow. | `{ balance_cents, currency }` |',
  '| `purchase_initiated` | A pack purchase succeeded. Sent immediately before `purchase_completed` — the purchase is one atomic step and your `/debit` has already answered. | `{ order_id }` |',
  '| `purchase_completed` | Same moment; carries the cards in the pack. | `{ order_id, items: [{ id, name, rarity }] }` |',
  '| `purchase_failed` | A purchase did not go through and nothing was charged. `code` is the error code when one is known, e.g. `INSUFFICIENT_BALANCE`, or your own `SPENDING_LIMIT_REACHED*` code from `/debit` — `message` is then the text the player sees. | `{ order_id, message, code? }` — `order_id` is `"unknown"` when no order was created |',
  '| `shipping_failed` | Only when **Low balance message** is set to `hidden` in the Design Constructor: a delivery could not be created because the balance does not cover its fee, and the game shows no message of its own. | `{ code, message }` — `code` is `INSUFFICIENT_BALANCE` |',
  '| `reveal_started` | The reveal screen opened for an order. | `{ order_id, item_count }` |',
  '| `item_revealed` | A card was turned over. | `{ order_id, item_id, name, fair_market_value }` — value in the session currency |',
  '| `item_sold` | The player took the instant buyback on a card. Your `/credit` is queued and follows asynchronously, shortly after this event. | `{ order_id, item_id, gross_price }` — price in the session currency |',
  '| `reveal_complete` | The player left the reveal screen. | `{ order_id }` |',
  '| `navigate_lobby` | The player pressed **Back to lobby** (needs `lobby_url` at launch). The game waits 300 ms for your `navigate_lobby_result`; without `handled: true` it navigates to `lobby_url` itself. | — |',
  '| `navigate_deposit` | The player pressed **Add funds** (needs `deposit_url` at launch). The game waits 300 ms for your `navigate_deposit_result`; without `handled: true` it opens `deposit_url` itself. | — |',
  '| `haptic` | Only after you sent `enable_haptics`. A moment worth a buzz: the pack is picked or the reel lands, a card is sold. | `{ intent }` — `light`, `medium`, `heavy` or `success` |',
  '| `navigate_back_result` | Answer to your `navigate_back`. `handled: false` means the player is already on the main pack screen and nothing was closed. | `{ handled }` |',
  '| `error` | The UI hit an unexpected error and showed its error screen. | `{ message, stack? }` |',
  '',
  '**Messages you can send** — post to the iframe’s `contentWindow` with `source: "repackz-parent"`:',
  '',
  '| Type | Effect |',
  '| --- | --- |',
  '| `balance_changed` | Re-read the balance now (your `get_balance` is called). Send it after a deposit made outside the game. Without it the iframe still refreshes when its tab regains focus. |',
  '| `navigate_lobby_result` | Answer to `navigate_lobby` with `payload: { handled: true }` when your page takes the player to the lobby itself — the game then stays put. `handled: false`, or no answer within 300 ms, and the game navigates to `lobby_url`. |',
  '| `navigate_deposit_result` | Same for `navigate_deposit` and your cashier. |',
  '| `enable_haptics` | Start receiving `haptic` events. Send it after `session_ready`, again on every iframe load. Off by default. |',
  '| `navigate_back` | Go back one step inside the game — close the open dialog, leave the reveal, a delivery form, My Packs or Just Pulled. Always answered with `navigate_back_result`. While a purchase or a sell is in flight the step is skipped and the answer is still `handled: true`. |',
  '',
  'A listener, in full:',
  '',
  '```js',
  "window.addEventListener('message', (e) => {",
  "  if (e.data?.source !== 'repackz') return;",
  '  switch (e.data.type) {',
  "    case 'session_expired': relaunch(); break;",
  "    case 'purchase_failed': if (e.data.payload.code === 'INSUFFICIENT_BALANCE') openCashier(); break;",
  "    case 'balance_updated': showBalance(e.data.payload.balance_cents); break;",
  '  }',
  '});',
  '```',
  '',
  '### Hardware back button',
  '',
  'Inside a mobile app WebView the Android back button closes the WebView by default, even when the',
  'player is deep in My Packs. The game switches screens without adding browser history entries, so',
  '`webView.canGoBack()` stays `false`. Send `navigate_back` from your back handler and close the',
  'WebView only when the game answers `handled: false`:',
  '',
  '```js',
  '// On your page around the iframe, called by your app when back is pressed',
  'function onHardwareBack() {',
  '  return new Promise((resolve) => {',
  '    const timer = setTimeout(() => done(false), 300); // no answer: treat as root',
  '    function done(handled) {',
  '      clearTimeout(timer);',
  "      window.removeEventListener('message', onMessage);",
  '      resolve(handled); // false → close the WebView',
  '    }',
  '    function onMessage(e) {',
  "      if (e.data?.source === 'repackz' && e.data.type === 'navigate_back_result') {",
  '        done(e.data.payload.handled);',
  '      }',
  '    }',
  "    window.addEventListener('message', onMessage);",
  '    iframe.contentWindow.postMessage(',
  "      { source: 'repackz-parent', type: 'navigate_back' },",
  "      '*',",
  '    );',
  '  });',
  '}',
  '```',
  '',
  'If the WebView loads the game URL directly (no page around it), post the same message to the',
  'game window itself and listen there: the game answers on `window.parent`, which for a top-level page',
  'is the page itself.',
  '',
  '### Lobby and cashier in a mobile app',
  '',
  '**Back to lobby** and **Add funds** navigate inside the iframe, where an app cannot intercept the',
  'load. Before navigating, the game posts `navigate_lobby` / `navigate_deposit` and waits 300 ms: answer',
  '`handled: true` and route the player yourself — the game does nothing else. Pages that do not answer',
  'keep the default navigation, so this is opt-in:',
  '',
  '```js',
  "window.addEventListener('message', (e) => {",
  "  if (e.data?.source !== 'repackz') return;",
  "  if (e.data.type !== 'navigate_lobby' && e.data.type !== 'navigate_deposit') return;",
  "  if (e.data.type === 'navigate_lobby') openLobbyScreen();",
  '  else openDepositScreen();',
  '  iframe.contentWindow.postMessage(',
  "    { source: 'repackz-parent', type: e.data.type + '_result', payload: { handled: true } },",
  "    '*',",
  '  );',
  '});',
  '```',
  '',
  '### Haptics in a mobile app',
  '',
  'Web pages cannot vibrate on iOS at all. Send `enable_haptics` and the game posts `haptic` at every',
  'moment it would buzz, leaving the feel to your app. `intent` maps onto the native APIs directly:',
  '',
  '| `intent` | iOS | Android |',
  '| --- | --- | --- |',
  '| `light` | `UIImpactFeedbackGenerator(style: .light)` | `HapticFeedbackConstants.CLOCK_TICK` |',
  '| `medium` | `UIImpactFeedbackGenerator(style: .medium)` | `HapticFeedbackConstants.VIRTUAL_KEY` |',
  '| `heavy` | `UIImpactFeedbackGenerator(style: .heavy)` | `HapticFeedbackConstants.LONG_PRESS` |',
  '| `success` | `UINotificationFeedbackGenerator` `.success` | `HapticFeedbackConstants.CONFIRM` |',
  '',
  '```js',
  '// On your page around the iframe — hand it to your app',
  "window.addEventListener('message', (e) => {",
  "  if (e.data?.source !== 'repackz') return;",
  "  if (e.data.type === 'session_ready') {",
  "    iframe.contentWindow.postMessage({ source: 'repackz-parent', type: 'enable_haptics' }, '*');",
  '  }',
  "  if (e.data.type === 'haptic') nativeBridge.haptic(e.data.payload.intent); // your WebView bridge",
  '});',
  '```',
  '',
  'On Android the game already vibrates through the browser when the WebView may (your app holds the',
  '`VIBRATE` permission). If you play `haptic` natively as well, the player feels both — handle it on',
  'one side only.',
  '',
];
